import moment from 'moment';
import {RefObject, useEffect, useRef, useState} from 'react';
import {Alert, Dimensions, FlatList, StyleSheet, Text, TouchableOpacity, View, Platform} from 'react-native';
import {ChevronIcon} from '~assets/icons';
import Button from '~components/Button';
import Spinner from '~components/Spinner';
import {Event, Recurrence} from '~types/api/event';
import {groupByDate} from '~Utils/Time';
import {useTheme, useThemedStyles} from '~contexts/ThemeContext';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import RBSheet from '../../lib/rbsheet';
import { logEventDatePickerViewed, logEventDateSelected } from '~Utils/firebaseAnalytics';

interface Props {
  refRBSheet: RefObject<any>;
  recurrences: Event[];
  onJoinEvent: (event_id: number, isLeave?: boolean, event?: any) => void;
  eventId?: string;
  eventType?: string;
}

type MonthItem = {
  month: string;
  year: string;
  monthNumeric: string;
};

type DayItem = {
  day: number;
  active: boolean;
  isPast: boolean;
  recurrence: {date: string; times: Recurrence[]};
};

const deviceWidth = Dimensions.get('window').width;
const cellWidth = (deviceWidth - 75) / 7;

const Calendar = ({refRBSheet, recurrences, onJoinEvent, eventId, eventType}: Props) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(createStyles);
  const [month, setMonth] = useState(new Date().toLocaleString('default', {month: 'long'}));
  const [year, setYear] = useState(moment(new Date()).format('YYYY'));
  const [monthArray, setMonthArray] = useState<Array<MonthItem>>([]);
  const [daysArray, setDaysArray] = useState<Array<DayItem>>([]);
  const [dateSelect, setDateSelect] = useState<DayItem>();
  const [selectedId, setSelectedId] = useState<number>();
  const [isSubscribed, setIsSubScribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [previousRecurrences, setPreviousRecurrences] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<Recurrence | null>(null);
  const insects = useSafeAreaInsets();

  useEffect(() => {
    if (eventId) {
      logEventDatePickerViewed(eventId, eventType || 'unknown');
    }
  }, [eventId]);

  const getMonthNames = (date = new Date()) => {
    setIsLoading(true);
    let d = new Date(+date);
    d.setDate(1);
    let result = [];
    for (let i = 0; i < 12; i++) {
      result.push({
        month: d.toLocaleString('default', {month: 'long'}),
        monthNumeric: d.toLocaleString('default', {month: 'numeric'}),
        year: moment(d).format('YYYY'),
      });
      d.setMonth(d.getMonth() + 1);
    }
    onMonthSelect(result[0]);
    setMonthArray(result);
  };

  const daysInMonth = (month: string, year: string) => {
    const groupByDateArray = groupByDate(recurrences);
    const dateMonth = moment(`${year}-${month}`, 'YYYY-MM');
    const totalDays = dateMonth.daysInMonth();
    const date = `01-${minTwoDigits(Number(month))}-${year}`;
    const monthDay = moment(date, 'DD-MM-YYYY').day();
    const today = moment().startOf('day');

    const daysArray = new Array(monthDay).fill({day: ''});

    for (let index = 1; index <= totalDays; index++) {
      const date = minTwoDigits(index);
      const dateString = `${year}-${minTwoDigits(Number(month))}-${date}`;
      const currentDate = moment(dateString, 'YYYY-MM-DD');
      const isPast = currentDate.isBefore(today);
      const isActive = groupByDateArray?.filter(
        (item: {date: string; times: Recurrence[]}) => item.date === dateString,
      );
      daysArray.push({
        day: index + '',
        active: isActive?.length > 0,
        isPast: isPast,
        recurrence: isActive[0],
      });
    }
    return daysArray;
  };

  const minTwoDigits = (n: Number) => {
    return ((n as number) < 10 ? '0' : '') + n;
  };

  useEffect(() => {
    // Check if recurrences actually changed to avoid unnecessary processing
    const recurrencesChanged = JSON.stringify(recurrences) !== JSON.stringify(previousRecurrences);
    
    if (recurrencesChanged) {
      setPreviousRecurrences(recurrences || []);
      setIsLoading(true);
      
      if (recurrences) {
        getMonthNames();
      } else {
        // If no recurrences, still setup the calendar but hide loader after a brief moment
        setTimeout(() => {
          setIsLoading(false);
        }, 300);
      }
    }
  }, [recurrences, previousRecurrences]);

  const onMonthSelect = (selectMonth: MonthItem) => {
    setIsLoading(true);
    setMonth(selectMonth.month);
    setYear(selectMonth.year);
    const daysArray = daysInMonth(selectMonth.monthNumeric, selectMonth.year);
    setDaysArray(daysArray);
    // Use setTimeout to ensure the UI updates and then hide loader
    setTimeout(() => {
      setIsLoading(false);
    }, 100);
  };

  const onDatePress = (item: DayItem) => {
    setDateSelect(item);
    setSelectedId(undefined);
    setSelectedEvent(null);
    setIsSubScribed(false);
    if (eventId && item.recurrence?.date) {
      logEventDateSelected(eventId, eventType || 'unknown', item.recurrence.date);
    }
    const availableSlots = item.recurrence?.times?.filter(slot => !slot.is_cancelled);
    console.log(availableSlots, 'availableSlotsavailableSlots');
    
    if (availableSlots?.length === 1) {
      const [slot] = availableSlots;
      setSelectedId(slot.event_id);
      setSelectedEvent(slot);
      setIsSubScribed(slot.subscribed);
      onJoinEvent(slot.event_id, slot.subscribed, slot);
      refRBSheet.current?.close();
      return;
    }
  };

  const onApplyClick = () => {
    if (!dateSelect || !dateSelect.day) {
      Alert.alert('Select date to join.');
      return;
    }

    if (!selectedId) {
      const availableSlots = dateSelect.recurrence?.times?.filter(slot => !slot.is_cancelled);
      if (availableSlots?.length === 1) {
        const [slot] = availableSlots;
        onJoinEvent(slot.event_id, slot.subscribed, slot);
        refRBSheet.current?.close();
        return;
      }
      Alert.alert('Select slot to join.');
      return;
    }
    onJoinEvent(selectedId, isSubscribed, selectedEvent);
    refRBSheet.current?.close();
  };


  const sheetHeight = Dimensions.get('window').height * 0.65;

  return (
    <RBSheet
      ref={refRBSheet}
      useNativeDriver={false}
      customStyles={{
        wrapper: styles.wrapperStyle,
        container: styles.bottomSheetStyle,
      }}
      customModalProps={{
        animationType: 'slide',
        statusBarTranslucent: true,
      }}
      height={sheetHeight}
      customAvoidingViewProps={{
        enabled: false,
      }}
      onOpen={() => {
        // Reset selections when calendar opens
        setDateSelect(undefined);
        setSelectedId(undefined);
        setIsSubScribed(false);
      }}>
      <View style={styles.bottomSheetContent}>
        {isLoading ? (
          <View style={styles.loaderContainer}>
            <Spinner size={40} spinnerBackgroundColor={colors.primary} />
            <Text style={styles.loaderText}>Loading...</Text>
          </View>
        ) : (
          <>
            {dateSelect && dateSelect.day ? (
              <View style={styles.headerTime}>
                <TouchableOpacity onPress={() => setDateSelect(undefined)}>
                  <ChevronIcon />
                </TouchableOpacity>
                <Text style={[styles.calendarTitle, {marginStart: 10, color: colors.black}]}>Select event time</Text>
              </View>
            ) : (
              <Text style={styles.calendarTitle}>Select event date</Text>
            )}
            {dateSelect && dateSelect.day ? (
              <View>
                <Text style={[styles.recurenceDateTitle, {color: colors.black}]}>{dateSelect.recurrence.date}</Text>
                <FlatList
                  data={dateSelect.recurrence.times}
                  style={{height: 230}}
                  renderItem={({item}) => {
                    return (
                      <TouchableOpacity
                        onPress={() => {
                          setSelectedId(item.event_id);
                          setSelectedEvent(item);
                          setIsSubScribed(item.subscribed);
                        }}
                        disabled={!!item.is_cancelled}
                        style={[
                          styles.slotItem,
                          item.event_id === selectedId && {borderColor: colors.eventInfluencer, borderWidth: 2},
                        ]}>
                        {item.is_cancelled ? (
                          <View>
                            <Text style={{color: colors.black}}>
                              {moment(item.start_date).format('hh:mm a')} - {moment(item.end_date).format('hh:mm a')}
                            </Text>
                            <Text style={{color: colors.error, marginTop: 5}}>
                              This event was cancelled by the organiser
                            </Text>
                          </View>
                        ) : (
                          <Text style={{color: colors.black}}>
                            {moment(item.start_date).format('hh:mm a')} - {moment(item.end_date).format('hh:mm a')}
                          </Text>
                        )}
                      </TouchableOpacity>
                    );
                  }}
                />
              </View>
            ) : (
              <View>
                <FlatList
                  data={monthArray}
                  horizontal
                  scrollEnabled={true}
                  nestedScrollEnabled={true}
                  style={[styles.monthList, {width: '100%'}]}
                  contentContainerStyle={{paddingHorizontal: 10}}
                  keyExtractor={(item, index) => `${item.month}-${item.year}-${index}`}
                  renderItem={({item}) => {
                    return (
                      <TouchableOpacity
                        onPress={() => onMonthSelect(item)}
                        style={[styles.monthTextTag, month === item.month && styles.selectMonth]}>
                        <Text style={[styles.monthText, month === item.month && styles.selectMonthText]}>{item.month}</Text>
                      </TouchableOpacity>
                    );
                  }}
                />
                <View style={{flexDirection: 'row', alignItems: 'center'}}>
                  <View style={[styles.datTextTag, {borderWidth: 0, backgroundColor: colors.statusGray}]}>
                    <Text style={styles.textDay}>S</Text>
                  </View>
                  <View style={[styles.datTextTag, {borderWidth: 0, backgroundColor: colors.statusGray}]}>
                    <Text style={styles.textDay}>M</Text>
                  </View>
                  <View style={[styles.datTextTag, {borderWidth: 0, backgroundColor: colors.statusGray}]}>
                    <Text style={styles.textDay}>T</Text>
                  </View>
                  <View style={[styles.datTextTag, {borderWidth: 0, backgroundColor: colors.statusGray}]}>
                    <Text style={styles.textDay}>W</Text>
                  </View>
                  <View style={[styles.datTextTag, {borderWidth: 0, backgroundColor: colors.statusGray}]}>
                    <Text style={styles.textDay}>T</Text>
                  </View>
                  <View style={[styles.datTextTag, {borderWidth: 0, backgroundColor: colors.statusGray}]}>
                    <Text style={styles.textDay}>F</Text>
                  </View>
                  <View style={[styles.datTextTag, {borderWidth: 0, backgroundColor: colors.statusGray}]}>
                    <Text style={styles.textDay}>S</Text>
                  </View>
                </View>
                <FlatList
                  data={daysArray}
                  showsVerticalScrollIndicator={false}
                  style={styles.dayList}
                  numColumns={7}
                  bounces={false}
                  renderItem={({item}) => {
                    if (!item.day) {
                      return <View style={[styles.datTextTag, {borderWidth: 0, backgroundColor: 'transparent'}]} />;
                    }
                    const isDisabled = !item.active || item.isPast;
                    return (
                      <TouchableOpacity
                        onPress={() => onDatePress(item)}
                        style={[
                          styles.datTextTag,
                          item.active && !item.isPast 
                            ? {backgroundColor: colors.eventInfluencer} 
                            : {backgroundColor: colors.gray100},
                        ]}
                        disabled={isDisabled}>
                        <Text
                          style={[
                            styles.monthText, 
                            item.active && !item.isPast 
                              ? {color: colors.white} 
                              : {color: colors.textTertiary}
                          ]}>
                          {minTwoDigits(item.day)}
                        </Text>
                      </TouchableOpacity>
                    );
                  }}
                />
              </View>
            )}
            <Button
              label={isSubscribed ? 'Leave' : 'Continue'}
              containerStyle={[styles.applyBtn, {bottom: insects.bottom + 10, width: '90%'}]}
              textStyle={styles.textStyle}
              onPress={onApplyClick}
            />
          </>
        )}
      </View>
    </RBSheet>
  );
};

export default Calendar;

const createStyles = (colors: any) =>
  StyleSheet.create({
    loaderContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: 60,
    },
    loaderText: {
      marginTop: 16,
      fontSize: 16,
      color: colors.textSecondary,
      fontWeight: '500',
    },
    textDay: {
      color: colors.white,
    },
    slotItem: {
      marginTop: 15,
      flexDirection: 'row',
      padding: 12,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 10,
      marginEnd: 20,
      backgroundColor: colors.surface,
    },
    recurenceDateTitle: {
      fontWeight: 'bold',
      fontSize: 16,
      marginTop: 20,
      color: colors.textPrimary,
    },
    headerTime: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    textStyle: {
      color: colors.white,
    },
    applyBtn: {
      backgroundColor: colors.secondary,
      width: deviceWidth - 40,
      borderRadius: 27.5,
      marginTop: 25,
      marginHorizontal: 20,
      position: 'absolute',
    },
    dayList: {
      marginTop: 5,
    },
    selectMonthText: {
      color: colors.white,
    },
    monthText: {
      fontWeight: '400',
      fontSize: 15,
      lineHeight: 18,
      color: colors.textPrimary,
    },
    selectMonth: {
      backgroundColor: colors.statusGray,
    },
    datTextTag: {
      borderColor: colors.border,
      borderWidth: 1,
      marginEnd: 6,
      borderRadius: 56,
      width: cellWidth,
      paddingVertical: 10,
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: 6,
    },
    monthTextTag: {
      paddingHorizontal: 12,
      paddingVertical: 9,
      borderColor: colors.border,
      borderWidth: 1,
      marginEnd: 6,
      borderRadius: 56,
      backgroundColor: colors.surface,
    },
    monthList: {
      marginTop: 15,
      marginBottom: 10,
      flexGrow: 0,
    },
    wrapperStyle: {
      backgroundColor: colors.overlayBackground,
    },
    bottomSheetContent: {
      flex: 1,
      paddingVertical: 10,
      paddingHorizontal: 10,
      paddingBottom: 30
    },
    bottomSheetStyle: {
      borderTopLeftRadius: 20,
      borderTopRightRadius: 20,
      backgroundColor: colors.surface,
    },
    calendarTitle: {
      fontWeight: '700',
      color: colors.textPrimary,
      fontSize: 17,
      lineHeight: 22,
    },
  });
